import React from "react";
import styled from "styled-components";
import { FaArrowRight, FaLock, FaServer, FaUsers } from "react-icons/fa";

const Section = styled.section`
  padding: 112px 0;
  background: #080a16;
  color: #ffffff;
  overflow: hidden;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 640px;
    height: 640px;
    right: -280px;
    top: -220px;
    border-radius: 50%;
    background: rgba(0, 200, 255, 0.08);
    filter: blur(90px);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    padding: 72px 0;
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 40px;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    padding: 0 20px;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(360px, 0.95fr);
  gap: 72px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 42px;
  }
`;

const Copy = styled.div``;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #00c8ff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2.4px;
  text-transform: uppercase;
  margin-bottom: 24px;

  &::before {
    content: "";
    display: block;
    width: 28px;
    height: 1px;
    background: #00c8ff;
  }
`;

const Title = styled.h2`
  max-width: 650px;
  font-size: clamp(2.2rem, 4vw, 4rem);
  line-height: 1.06;
  letter-spacing: -1.8px;
  margin: 0 0 24px;
  font-weight: 800;

  span {
    color: #00c8ff;
  }
`;

const Lead = styled.p`
  max-width: 610px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 1.08rem;
  line-height: 1.75;
  margin: 0 0 32px;
`;

const Emphasis = styled.strong`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 650;
`;

const Proof = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 22px;
  margin-bottom: 36px;
`;

const ProofItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;

  svg {
    color: #00c8ff;
    font-size: 13px;
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-radius: 9px;
  background: #00c8ff;
  color: #07101c;
  text-decoration: none;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.2px;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;

  svg {
    transition: transform 0.25s ease;
  }

  &:hover {
    background: #38d5ff;
    transform: translateY(-2px);
    box-shadow: 0 14px 34px rgba(0, 200, 255, 0.22);

    svg {
      transform: translateX(4px);
    }
  }
`;

const Media = styled.div`
  position: relative;
  min-height: 390px;
  border: 1px solid rgba(0, 200, 255, 0.2);
  border-radius: 18px;
  overflow: hidden;
  background: #111526;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.36);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(115deg, rgba(5, 8, 20, 0.08), rgba(5, 8, 20, 0.7));
    pointer-events: none;
  }

  @media (max-width: 600px) {
    min-height: 300px;
  }
`;

const ServerImage = styled.img`
  width: 100%;
  height: 100%;
  min-height: 390px;
  object-fit: cover;
  display: block;

  @media (max-width: 600px) {
    min-height: 300px;
  }
`;

const MediaLabel = styled.div`
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 22px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 16px;

  @media (max-width: 600px) {
    left: 18px;
    right: 18px;
    bottom: 18px;
  }
`;

const LabelCopy = styled.div`
  span {
    display: block;
    color: #00c8ff;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    margin-bottom: 7px;
  }

  strong {
    display: block;
    color: #ffffff;
    font-size: 18px;
    line-height: 1.2;
  }
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.72);
  font-size: 11px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(5, 8, 20, 0.58);
  padding: 8px 10px;
  border-radius: 6px;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #38d996;
    box-shadow: 0 0 10px rgba(56, 217, 150, 0.85);
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

export const PrivateIT = () => (
  <Section id="private-it">
    <Inner>
      <Layout>
        <Copy>
          <Eyebrow>Nueva línea de Monaco</Eyebrow>
          <Title>
            Tu empresa puede tener su propia <span>área TI</span>.
          </Title>
          <Lead>
            Un equipo digital que recibe solicitudes, resuelve problemas y
            mejora tus sistemas todos los días. <Emphasis>Con IAs privadas,
            datos internos y servidores físicos operados por Monaco.</Emphasis>
          </Lead>
          <Proof>
            <ProofItem><FaServer /> Servidores físicos dedicados</ProofItem>
            <ProofItem><FaLock /> Datos fuera del entrenamiento público</ProofItem>
            <ProofItem><FaUsers /> Supervisión de expertos Monaco</ProofItem>
          </Proof>
          <Button href="/area-ti-privada/">
            Conocer Área TI Privada <FaArrowRight />
          </Button>
        </Copy>

        <Media>
          <ServerImage
            src="/area-ti-privada/monaco-private-servers.webp"
            alt="Servidores físicos dedicados para Área TI Privada de Monaco"
            loading="lazy"
          />
          <MediaLabel>
            <LabelCopy>
              <span>Monaco IT Services</span>
              <strong>Infraestructura propia.<br />Equipo siempre disponible.</strong>
            </LabelCopy>
            <Status>Operación privada</Status>
          </MediaLabel>
        </Media>
      </Layout>
    </Inner>
  </Section>
);
